import { Overlay } from 'ol';
import { unByKey } from 'ol/Observable';
import { Coordinate } from 'ol/coordinate';
import { generateUniqueID } from 'macaw';
import { getAvatarShades } from 'woodpecker';
import { easeOut } from 'ol/easing';
import { NotesType } from '../../../store/notesStore';
import { notesStore } from '../../utilityclasses/NotesStoreListener';
import { globalStore } from '../../utilityclasses/AppStoreListener';
import MapBase from '../../mapLayer/mapBase';
import ToolAbstract from '../../utilityclasses/ToolAbstractClass';
import { isOutOfExtent } from '../../../helpers/helpers';
import { TOOL_TYPE } from '../constants';

class AddNotesBPT extends ToolAbstract {
  private mapObj: MapBase;

  private contextMenuElement: any;

  private eventRef: any;

  private notesBoxOverlay: Overlay;

  constructor(mapObj: MapBase) {
    super();
    this.mapObj = mapObj;
    this.contextMenuElement = document.getElementById('notes-box');
    this.eventRef = null;
    this.notesBoxOverlay = new Overlay({
      element: document.getElementById('notes-box') as any,
      positioning: 'center-center',
      className: 'notes-box-overlay'
    });
  }

  init() {
    this.eventRef = this.mapObj?.map?.on('click', e => this.clickHandle(e));

    this.mapObj?.map?.addOverlay(this.notesBoxOverlay);

    this.contextMenuElement = document.getElementById('notes-box');
    if (this.contextMenuElement) {
      this.contextMenuElement.style.zIndex = '10';
      this.notesBoxOverlay?.setElement(this.contextMenuElement);
    }
    notesStore?.AppStore?.updateSelectedNote();
  }

  addNoteToOverlay(note: NotesType) {
    const coordinates = [note.lon, note.lat];
    const element = document.createElement('div');

    const email = note?.user_details?.email || 'anonymousUser';
    element.innerHTML = email[0].toUpperCase();
    element.classList.add('talkbubble');
    element.style.backgroundColor = getAvatarShades(email);

    const overlay = new Overlay({
      element,
      positioning: 'center-center',
      position: coordinates,
      className: 'talkbubble-overlay'
    });

    overlay.set('note_data', {
      tool_id: note.id
    });

    element?.addEventListener('click', () => {
      notesStore?.AppStore?.setSelectedNote(overlay?.get('note_data')?.tool_id || '');
      this.setPositionToNote(overlay?.getPosition() as Coordinate);
    });

    this.mapObj?.map?.addOverlay(overlay);

    this.mapObj.map!.getViewport().style.cursor = 'arrow';
  }

  setPositionToNote(coordinate: Coordinate) {
    this.contextMenuElement = document.getElementById('notes-box');
    if (!this.contextMenuElement) {
      this.init();
    }
    if (this.contextMenuElement) {
      this.contextMenuElement.style.display = 'block';
      this.notesBoxOverlay?.setElement(this.contextMenuElement);
    }

    this.adjustOverlayPosition(coordinate);
  }

  getGeojson(coordinate: Coordinate) {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coordinate
          },
          id: generateUniqueID()
        }
      ]
    };
  }

  clickHandle(event: any) {
    if (isOutOfExtent(event, this.mapObj.map)) return;

    this.contextMenuElement = document.getElementById('notes-box');
    if (
      !this.contextMenuElement ||
      !this.notesBoxOverlay ||
      globalStore.AppStore?.tool?.tool_id !== TOOL_TYPE.NOTES_BPT
    ) {
      return;
    }

    this.notesBoxOverlay?.setElement(this.contextMenuElement);
    this.notesBoxOverlay?.setPosition(event.coordinate);
    this.contextMenuElement.style.display = 'block';

    this.adjustOverlayPosition(event.coordinate);
    const geojson = this.getGeojson(event.coordinate);
    const [lon, lat] = event.coordinate;

    const tempNote: Partial<NotesType> = {
      id: geojson?.features[0]?.id,
      lon,
      lat,
      category: 0,
      access_type: 0,
      count: 0,
      text: '',
      created_at: new Date().toISOString(),
      status: 0,
      user_details: {
        id: '',
        email: '',
        full_name: ''
      },
      assignee_details: []
    };

    notesStore?.AppStore?.updateState(() => ({
      selectedNote: tempNote as NotesType,
      selectedNoteToolID: geojson?.features[0]?.id
    }));
  }

  /**
   * Adjusts overlay position if it's too close to the map edges
   * @param {Array<number>} position - Initial position of the overlay
   */
  adjustOverlayPosition(position: number[]) {
    const mapSize = this.mapObj.map?.getSize() || [];
    const pixelPosition = this.mapObj.map?.getPixelFromCoordinate(position) || [];

    // Offset for buffer space from the edges
    const buffer = 10;

    // Get overlay dimensions
    const overlayWidth = this.contextMenuElement.offsetWidth;
    const overlayHeight = this.contextMenuElement.offsetHeight;

    // Get panel widths
    const rightPanelWidth = document.getElementById('worksheet-panel')?.offsetWidth || 320;
    const leftPanelWidth = document.getElementById('layer-panel-container')?.offsetWidth || 0;

    // Calculate adjusted pixel position
    let adjustedPixelX = pixelPosition[0];
    let adjustedPixelY = pixelPosition[1];

    // Then check if the overlay would extend beyond the right panel
    if (pixelPosition[0] + overlayWidth + buffer > mapSize[0] - rightPanelWidth) {
      // If it would extend into right panel, position it to avoid that
      adjustedPixelX = mapSize[0] - overlayWidth - rightPanelWidth - buffer;
    }
    // Handle X-axis positioning
    // First check if the original click position is within the left panel area
    if (pixelPosition[0] < leftPanelWidth) {
      // If click was in left panel area, move it to the right of the panel
      adjustedPixelX = leftPanelWidth + buffer;
    }

    // Handle Y-axis positioning
    if (pixelPosition[1] + overlayHeight + buffer > mapSize[1]) {
      adjustedPixelY = mapSize[1] - overlayHeight - buffer;
    } else if (pixelPosition[1] < buffer + overlayHeight / 2) {
      adjustedPixelY = overlayHeight / 2 + buffer;
    }

    // Additional check - ensure the overlay doesn't overlap with left panel
    if (adjustedPixelX - overlayWidth / 2 < leftPanelWidth) {
      adjustedPixelX = leftPanelWidth + overlayWidth / 2;
    }

    // Convert adjusted pixel position back to map coordinates
    const adjustedPosition = this.mapObj.map?.getCoordinateFromPixel([adjustedPixelX, adjustedPixelY]);

    // Update overlay position
    this.notesBoxOverlay?.setPosition(adjustedPosition);
  }

  removeNotes() {
    const overlays = [...(this.mapObj.map?.getOverlays()?.getArray() || [])];
    overlays.forEach(overlay => {
      if (overlay?.get('note_data')) {
        this.mapObj.map?.removeOverlay(overlay);
      }
    });
    if (this.eventRef) unByKey(this.eventRef);
  }

  showNoteCard(note: NotesType | null, shouldScrollToView = false) {
    // If note is null, just show an empty note form at the current overlay position
    if (!note) {
      if (this.contextMenuElement) {
        this.contextMenuElement.style.display = 'block';
      }

      // Make sure overlay is attached to the map
      if (
        this.mapObj.map &&
        this.notesBoxOverlay &&
        !this.mapObj.map.getOverlays().getArray().includes(this.notesBoxOverlay)
      ) {
        this.mapObj.map.addOverlay(this.notesBoxOverlay);
      }

      return;
    }

    const coordinates = [note.lon, note.lat];
    if (shouldScrollToView) {
      const view = this.mapObj?.map?.getView();
      if (view) {
        view.animate({
          center: coordinates,
          duration: 100,
          easing: easeOut
        });
      }

      if (coordinates) {
        setTimeout(() => this.setPositionToNote(coordinates), 150);
      }
    } else if (coordinates) {
      this.setPositionToNote(coordinates);
    }
  }

  /**
   * Turns off the draw interaction by removing it from the map and removing event listeners.
   */
  off() {
    if (this.contextMenuElement) {
      this.contextMenuElement.style.display = 'none';
    }
    if (this.notesBoxOverlay) this.mapObj?.map?.removeOverlay(this.notesBoxOverlay);
    this.mapObj.map!.getViewport().style.cursor = '';
    notesStore?.AppStore?.updateState(() => ({
      selectedNote: null,
      selectedNoteToolID: ''
    }));
  }
}

export default AddNotesBPT;
